import { Project } from '@/types/project';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreVertical, Pencil, Trash2, FolderOpen } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface ProjectListProps {
  projects: Project[];
  onSelectProject: (project: Project) => void;
  onRenameProject: (projectId: string) => void;
  onDeleteProject: (projectId: string) => void;
}

export function ProjectList({
  projects,
  onSelectProject,
  onRenameProject,
  onDeleteProject,
}: ProjectListProps) {
  if (projects.length === 0) {
    return null;
  }

  return (
    <div className="mt-12 w-full overflow-hidden">
      <h3 className="text-xl font-display mb-4 text-muted-foreground">
        Recent Projects
      </h3>
      <div className="grid gap-3 w-full">
        {projects.map((project) => (
          <Card
            key={project.id}
            className="p-4 hover:bg-secondary/30 transition-colors cursor-pointer group w-full overflow-hidden"
            onClick={() => onSelectProject(project)}
          >
            <div className="flex items-center justify-between gap-2 w-full overflow-hidden">
              <div className="flex items-center gap-3 flex-1 min-w-0 overflow-hidden">
                <FolderOpen className="w-5 h-5 text-primary shrink-0" />
                <div className="min-w-0 flex-1">
                  <h4 className="font-medium truncate max-w-[200px] sm:max-w-none">{project.name}</h4>
                  <p className="text-sm text-muted-foreground truncate">
                    {project.rigs.length} {project.rigs.length === 1 ? 'rig' : 'rigs'} • Updated{' '}
                    {formatDistanceToNow(new Date(project.updatedAt), { addSuffix: true })}
                  </p>
                </div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onRenameProject(project.id);
                    }}
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteProject(project.id);
                    }}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
